import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Button
} from '@mui/material';
import {
  Share as ShareIcon,
  LibraryBooks as LibraryBooksIcon
} from '@mui/icons-material';
import ChampionsLayout from '../../components/layout/ChampionsLayout';
import { getMyTrackingStats } from '../../components/referral/referralUtils';
import api from '../../services/api';

const ChampionsDashboard = () => {
  const navigate = useNavigate();
  const [totalInteractions, setTotalInteractions] = useState(null);
  const [approvedQuestionsCount, setApprovedQuestionsCount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [statsRes, questionsRes] = await Promise.all([
          getMyTrackingStats(0),
          api.get('/exams/questions/?mine=true&status=published&page=1&page_size=1&list=1')
        ]);
        if (cancelled) return;
        const regs = statsRes.registrations ?? 0;
        setTotalInteractions(regs);
        const total = questionsRes.data?.total_count ?? 0;
        setApprovedQuestionsCount(total);
      } catch {
        if (!cancelled) {
          setTotalInteractions(0);
          setApprovedQuestionsCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const stats = [
    {
      title: 'Total no of approved questions',
      value: loading ? '—' : String(approvedQuestionsCount ?? 0),
      icon: <LibraryBooksIcon />,
      color: 'primary.main'
    },
    {
      title: 'Total Interactions',
      value: loading ? '—' : String(totalInteractions ?? 0),
      icon: <ShareIcon />,
      color: 'secondary.main'
    }
  ];

  return (
    <ChampionsLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Welcome to Champions Dashboard
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Manage your content and track your referrals
          </Typography>
        </Box>

        {/* Top row: two metric cards, equal width */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {stats.map((stat, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  minHeight: 160,
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3, px: 2 }}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      mx: 'auto',
                      mb: 1.5,
                      bgcolor: stat.color,
                      color: 'white'
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {stat.title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom row: two action cards, equal width */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}
            >
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    <LibraryBooksIcon />
                  </Avatar>
                }
                title="Question Bank"
                subheader="Create and manage educational content"
                titleTypographyProps={{ fontWeight: 600 }}
              />
              <CardContent sx={{ flex: 1, pt: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Access your question bank to create, edit, and organize educational content.
                  Submit questions for review and track their status in real-time.
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/champions/questionbank')}
                  fullWidth
                >
                  Manage Content
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3
                }
              }}
            >
              <CardHeader
                avatar={
                  <Avatar sx={{ bgcolor: 'secondary.main' }}>
                    <ShareIcon />
                  </Avatar>
                }
                title="Referral Management"
                subheader="Track and manage your referrals"
                titleTypographyProps={{ fontWeight: 600 }}
              />
              <CardContent sx={{ flex: 1, pt: 0 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Monitor your referral activities, track performance, and manage your network.
                  View detailed analytics and insights to grow your influence.
                </Typography>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => navigate('/champions/referral')}
                  fullWidth
                >
                  View Analytics
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ChampionsLayout>
  );
};

export default ChampionsDashboard;
